// components/ui/Badge.tsx
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { motion } from 'framer-motion';
import { CSSProperties } from 'react';

interface BadgeProps {
    variant?: 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'discount' | 'teal' | 'premium';
    size?: 'sm' | 'md' | 'lg';
    children: React.ReactNode;
    className?: string;
    pulse?: boolean;
    icon?: React.ReactNode;
    glow?: boolean;
    style?: CSSProperties;
}

export function Badge({
    variant = 'neutral',
    size = 'md',
    children,
    className,
    pulse,
    icon,
    glow,
    style,
}: BadgeProps) {
    const variants: Record<string, { bg: string; text: string; border: string; dot: string }> = {
        success: {
            bg: 'bg-emerald-50 dark:bg-emerald-950/40',
            text: 'text-emerald-700 dark:text-emerald-300',
            border: 'border-emerald-200 dark:border-emerald-800/60',
            dot: 'bg-emerald-500',
        },
        warning: {
            bg: 'bg-amber-50 dark:bg-amber-950/40',
            text: 'text-amber-700 dark:text-amber-300',
            border: 'border-amber-200 dark:border-amber-800/60',
            dot: 'bg-amber-500',
        },
        danger: {
            bg: 'bg-rose-50 dark:bg-rose-950/40',
            text: 'text-rose-700 dark:text-rose-300',
            border: 'border-rose-200 dark:border-rose-800/60',
            dot: 'bg-rose-500',
        },
        info: {
            bg: 'bg-blue-50 dark:bg-blue-950/40',
            text: 'text-blue-700 dark:text-blue-300',
            border: 'border-blue-200 dark:border-blue-800/60',
            dot: 'bg-blue-500',
        },
        neutral: {
            bg: 'bg-slate-100 dark:bg-slate-800',
            text: 'text-slate-600 dark:text-slate-300',
            border: 'border-slate-200 dark:border-slate-700',
            dot: 'bg-slate-400 dark:bg-slate-500',
        },
        discount: {
            bg: 'bg-violet-50 dark:bg-violet-950/40',
            text: 'text-violet-700 dark:text-violet-300',
            border: 'border-violet-200 dark:border-violet-800/60',
            dot: 'bg-violet-500',
        },
        teal: {
            bg: 'bg-teal-50 dark:bg-teal-950/40',
            text: 'text-teal-700 dark:text-teal-300',
            border: 'border-teal-200 dark:border-teal-800/60',
            dot: 'bg-teal-500',
        },
        premium: {
            bg: 'bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/50 dark:to-orange-950/50',
            text: 'text-amber-800 dark:text-amber-300',
            border: 'border-amber-300 dark:border-amber-700/60',
            dot: 'bg-amber-500',
        },
    };

    const sizes: Record<string, string> = {
        sm: 'px-2 py-0.5 text-[10px] gap-1',
        md: 'px-2.5 py-1 text-xs gap-1.5',
        lg: 'px-3 py-1.5 text-sm gap-2',
    };

    const v = variants[variant];

    return (
        <motion.span
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            whileHover={{ scale: 1.05 }}
            style={style}
            className={twMerge(clsx(
                'inline-flex items-center font-bold rounded-full border shadow-sm transition-all duration-300',
                v.bg,
                v.text,
                v.border,
                sizes[size],
                glow && 'shadow-lg',
                glow && variant === 'success' && 'shadow-emerald-500/20',
                glow && variant === 'warning' && 'shadow-amber-500/20',
                glow && variant === 'danger' && 'shadow-rose-500/20',
                glow && variant === 'info' && 'shadow-blue-500/20',
                pulse && 'animate-pulse',
                className
            ))}
        >
            <span className={clsx(
                'w-1.5 h-1.5 rounded-full flex-shrink-0',
                v.dot,
                pulse && 'animate-ping'
            )} />
            {icon && <span className="flex-shrink-0">{icon}</span>}
            <span className="truncate">{children}</span>
        </motion.span>
    );
}